import React, { FC, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { withProcedureWraper } from "../..";
import { Routes } from "../../../../AppRoutes";
import { Box } from "../../../../components/box";
import { Icon } from "../../../../components/icon";
import { getDDMMYYYFormat, isDateInRange } from "../../../../helper/dateTimeHelper";
import CreateRequestButton from "../../components/CreateRequestButton";
import { AiOutlineFilter, AiOutlineWarning } from "react-icons/ai";
import { compareRequestStatus, getRequestStatusHasAssigneeOptions } from "../../utils";
import moment, { Moment } from "moment";
import { ExportFileExcel } from "../../info-technology-process/components";
import { Checkbox, Space } from "antd";
import ExcelPrintButton from "../../components/ExcelPrintButton";
import { useSercurityControlCardColumn } from "./hooks/use-security-control-card-column";
import { useSecurityControlCardRequests } from "./hooks";
import { Table, UserManual } from "../../../../components";

export const procedureIssuingLongTermSecurityControlCard: FC = withProcedureWraper(() => {
  const history = useHistory();
  const { columns, handleRequestExportClick, isShortTermSelect, checkShortTerm, requestCheckbox, handleCheckboxClick } =
    useSercurityControlCardColumn();
  const {
    mappedSecurityControlCardMyRequests,
    mappedSecurityControlCardRequests = [],
    isLoading,
    guideLink,
  } = useSecurityControlCardRequests();

  const getCardTypeLabel = (cardTerm: string, cardType: string) => {
    if (cardTerm === "longTerm") {
      switch (cardType) {
        case "evict":
          return "Gia hạn";
        case "provide":
          return "Cấp mới";
        case "renew":
          return "Cấp lại";
        default:
          return "";
      }
    } else {
      switch (cardType) {
        case "provide":
          return "Cấp thẻ";
        case "renew":
          return "Cấp lại thẻ";
        default:
          return "";
      }
    }
  };

  const [filters, setFilters] = useState<any>({
    createdDateTime: null,
    eRequestStatus: null,
    eWarningStatus: null,
  });

  const filteredDataSource = useMemo(() => {
    let dataSource = [...mappedSecurityControlCardRequests];
    if (filters.eRequestStatus) {
      dataSource = dataSource.filter((e: any) => compareRequestStatus(e.eRequestStatus, filters?.eRequestStatus!));
    }
    if (filters.createdDateTime) {
      const [startDate, endDate]: [Moment, Moment] = filters.createdDateTime;
      dataSource = dataSource.filter((e: any) => {
        const createDate = moment(e.createdDateTime);
        return isDateInRange(createDate, startDate.startOf("day"), endDate.endOf("day"));
      });
    }

    if (filters.eWarningStatus) {
      dataSource = dataSource.filter((record) => {
        const content = record.eContent ? JSON.parse(record.eContent) : null;
        if (content == null || !content[0]?.expiredDate) return false;

        const expiredDate = moment(content[0].expiredDate);
        const maxDate = moment().add(8, "days");

        var isExpired = expiredDate.isBefore(maxDate, "day");

        if (filters.eWarningStatus === "autoRenew") return isExpired && record.isAutoRenew;
        return isExpired;
      });
    }

    return dataSource;
  }, [filters, mappedSecurityControlCardRequests]);

  const isShowNotification = useMemo(() => {
    const listRequest = [...mappedSecurityControlCardRequests];

    return listRequest.some((record) => {
      const content = record.eContent ? JSON.parse(record.eContent) : null;
      if (content == null) return false;
      return moment(content[0].expiredDate).isAfter(moment().add(record.eStage === "shortTerm" ? 8 : 10, "days"));
    });
  }, [mappedSecurityControlCardRequests]);

  const requestStatusHasAssigneeOptions = getRequestStatusHasAssigneeOptions("Đã cấp mới");

  const flattedData = mappedSecurityControlCardRequests.map((record: any) => ({
    createdDateTime: getDDMMYYYFormat(record.createdDateTime),
    eRequestStatus: record.eRequestStatus,
    eRequestPersonName: record.createdByName,
    ePhoneNumber: record.ePhoneNumber,
    eDepartment: record.eDepartment,
    eUnit: record.eUnit,
    eBaseId: record.eBaseId,
    ePurpose: record.ePurpose,
    eSuggestion: getCardTypeLabel(record.eStage, record.eSuggestion),
    eRequestStatusText: requestStatusHasAssigneeOptions.find((e) => e.value === record.eRequestStatus)?.label,
  }));

  const requestsColumn = [
    ...columns,
    {
      title: "Chọn in",
      dataIndex: "exportExcel",
      key: "exportExcel",
      render: (text: string, record: any) => {
        const isShortTerm = checkShortTerm(record);
        const checked = requestCheckbox.checked(record.id);
        let disabled: boolean;

        if (isShortTermSelect === null) {
          disabled = false;
        } else {
          disabled = isShortTerm === isShortTermSelect ? false : true;
        }

        return (
          <Checkbox
            className="ml-4"
            checked={checked}
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onChange={() => {
              handleCheckboxClick(record);
            }}
          />
        );
      },
    },
  ];

  return (
    <div className="ITCodeGrantedPage">
      <div className="pd15">
        <UserManual title="CẤP THẺ KIỂM SOÁT AN NINH" icon={<Icon.IdCard />} guideLink={guideLink} />
        <Box
          title="Yêu cầu của tôi"
          titleRight={
            <CreateRequestButton
              onClick={() => history.push(Routes.createProcedureSecurityControlCardPage.path.replace(":id", "0"))}
            />
          }
        >
          <Table
            loading={isLoading}
            rowKey="id"
            columns={columns}
            dataSource={mappedSecurityControlCardMyRequests}
            onRow={(record) => ({
              onClick: () => history.push(Routes.detailSercurityCardUnitsPage.path.replace(":id", record.id)),
              style: { cursor: "pointer" },
            })}
          />
        </Box>

        <Box
          title={
            <Space>
              <b>Các yêu cầu cần duyệt</b>
              {isShowNotification && (
                <div className="px-4 d-flex">
                  <AiOutlineWarning color="#f55167" fontSize={20} />
                  <span style={{ color: "#f55167" }}>Có cảnh báo</span>
                </div>
              )}
            </Space>
          }
          titleRight={
            <div className="d-flex column">
              <ExcelPrintButton count={requestCheckbox.length} onClick={handleRequestExportClick} />
              <ExportFileExcel
                exportContent={{
                  fileName: "Danh sach yeu cau can duyet cap the KSAN cho noi bo VIAGS",
                  content: [
                    { header: "Loại thẻ", dataIndex: "eSuggestion" },
                    {
                      header: "Người yêu cầu",
                      dataIndex: "eRequestPersonName",
                    },
                    {
                      header: "SĐT Người yêu cầu",
                      dataIndex: "ePhoneNumber",
                    },
                    { header: "Cơ quan/Đơn vị", dataIndex: "eDepartment" },
                    { header: "Đơn vị", dataIndex: "eUnit" },
                    { header: "Căn cứ theo nhu cầu", dataIndex: "eBaseId" },
                    { header: "Mục đích", dataIndex: "ePurpose" },
                    { header: "Trạng thái", dataIndex: "eRequestStatusText" },
                  ],
                }}
                data={flattedData}
                initialValues={filters}
                requestStatusOptions={requestStatusHasAssigneeOptions}
                onChange={(_, allValues: any) => setFilters(allValues)}
              >
                <div>
                  <CreateRequestButton label="Bộ lọc" prefixIcon={<AiOutlineFilter />} />
                </div>
              </ExportFileExcel>
            </div>
          }
        >
          <Table
            loading={isLoading}
            rowKey="id"
            columns={requestsColumn}
            dataSource={filteredDataSource}
            onRow={(record) => ({
              onClick: () => history.push(Routes.detailSercurityCardUnitsPage.path.replace(":id", record.id)),
              style: { cursor: "pointer" },
            })}
          />
        </Box>
      </div>
    </div>
  );
});
